import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  CheckmarkSolidIcon,
  EditIcon,
  Grid,
  ResetTimeIcon,
  Typography,
  XMarkIcon,
  useToaster
} from "@dealer-center/core-components";
import DashboardDealerInfoCard from "./dealerInfoCard";
import { DealerAddress } from "./dealerAddress";
import { DealerContact } from "./dealerContact";
import { DealerHours } from "./dealerHours";
import { useCC9Flags } from "@dealer-center/core-third-party-launch-darkly";
import { useNavigate, useOutletContext } from "react-router-dom";
import type { AccountEntityExtended } from "@dealer-center/ddc-api-account-interface";
import {
  useAccountFranchises,
  useFranchiseChanges
} from "@dealer-center/ddc-pages-franchise-management-fetch";

import type {
  IFranchiseFailureAlert,
  IOverviewProps
} from "@dealer-center/ddc-pages-dashboard-interface";

export const DashboardOverview: React.FC<IOverviewProps> = props => {
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [franchiseFailureAlert, setFranchiseFailureAlert] =
    useState<IFranchiseFailureAlert>({
      visible: false,
      addFailures: [],
      removeFailures: []
    });
  const [franchiseSuccessAlert, setFranchiseSuccessAlert] = useState<{
    visible: boolean;
    message: string;
    key: number;
  }>({
    visible: false,
    message: "",
    key: 0
  });
  const {
    "css.franchise-management.enable": enableFranchiseManagement,
    "css.dashboard-overview.audit-history": showAuditHistoryButton
  } = useCC9Flags();
  const navigate = useNavigate();
  const { label = "Overview" } = props;
  const { accountEntity } = useOutletContext<{
    accountEntity: AccountEntityExtended;
  }>();
  const toast = useToaster();

  const handleSuccess = useCallback((message: string) => {
    setFranchiseSuccessAlert(prevState => ({
      visible: true,
      message: "Account changes saved successfully",
      key: prevState.key + 1
    }));
  }, []);

  const handleError = useCallback(
    (error: string) => {
      toast.toastError("Error saving account changes");
    },
    [toast]
  );

  const handlePartialFailure = useCallback(
    (
      addFailures: { franchiseName: string; reason: string }[],
      removeFailures: { franchiseName: string; reason: string }[]
    ) => {
      setFranchiseFailureAlert({
        visible: true,
        addFailures,
        removeFailures
      });
    },
    []
  );

  const handleAlertDismiss = useCallback(() => {
    setFranchiseFailureAlert({
      visible: false,
      addFailures: [],
      removeFailures: []
    });
  }, []);

  // Auto-hide success alert after 5 seconds
  useEffect(() => {
    if (!franchiseSuccessAlert.visible) return;

    const timer = setTimeout(() => {
      setFranchiseSuccessAlert(prev => ({
        ...prev,
        visible: false
      }));
    }, 5000);

    return () => clearTimeout(timer);
  }, [franchiseSuccessAlert.key]); // Only re-run when key changes (new alert)

  // Parent fetches and initializes franchise data
  const {
    data: accountFranchises = [],
    isFetching: isFetchingAccountFranchises,
    isError: isAccountFranchisesError
  } = useAccountFranchises(accountEntity.id, {
    enabled: enableFranchiseManagement
  });

  // Memoize the franchise names array to prevent unnecessary re-renders
  const initialFranchiseNames = useMemo(
    () => (accountFranchises || []).map(f => f.name),
    [accountFranchises]
  );

  const franchiseChanges = useFranchiseChanges({
    accountId: accountEntity.id,
    initialFranchises: initialFranchiseNames,
    onSuccess: handleSuccess,
    onError: handleError,
    onPartialFailure: handlePartialFailure
  });

  useEffect(() => {
    if (isEdit && !isFetchingAccountFranchises) {
      franchiseChanges.initializeFranchises(initialFranchiseNames);
    }
  }, [isEdit, initialFranchiseNames, isFetchingAccountFranchises]);

  useEffect(() => {
    if (isAccountFranchisesError) {
      toast.toastError("Error loading account franchises");
    }
  }, [isAccountFranchisesError]);

  return (
    <>
      {franchiseSuccessAlert.visible && (
        <Alert
          key={franchiseSuccessAlert.key}
          role="alert"
          sx={{
            mb: 2
          }}
          type="success"
        >
          <Typography variant="h6">
            <strong>{franchiseSuccessAlert.message}</strong>
          </Typography>
        </Alert>
      )}
      {franchiseFailureAlert.visible &&
        (franchiseFailureAlert.addFailures.length > 0 ||
          franchiseFailureAlert.removeFailures.length > 0) && (
          <Alert
            role="alert"
            sx={{
              mb: 2,
              "& .MuiAlert-message": {
                width: "100%"
              }
            }}
            type="error"
          >
            <Typography variant="h6">
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
              >
                <strong>Some franchise changes could not be saved</strong>
                <XMarkIcon
                  style={{ cursor: "pointer" }}
                  title="Close"
                  onClick={handleAlertDismiss}
                />
              </Grid>
            </Typography>

            {franchiseFailureAlert.addFailures.length > 0 && (
              <div style={{ marginTop: "12px" }}>
                <Typography sx={{ fontWeight: 600, mb: 1 }} variant="h6">
                  Failed to add:
                </Typography>
                <ul style={{ margin: "4px 0 0 0", paddingLeft: "1.5rem" }}>
                  {franchiseFailureAlert.addFailures.map(failure => (
                    <li key={`add-${failure.franchiseName}`}>
                      <strong>{failure.franchiseName}</strong>: {failure.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {franchiseFailureAlert.removeFailures.length > 0 && (
              <div style={{ marginTop: "12px" }}>
                <Typography sx={{ fontWeight: 600, mb: 1 }} variant="h6">
                  Failed to remove:
                </Typography>
                <ul style={{ margin: "4px 0 0 0", paddingLeft: "1.5rem" }}>
                  {franchiseFailureAlert.removeFailures.map(failure => (
                    <li key={`remove-${failure.franchiseName}`}>
                      <strong>{failure.franchiseName}</strong>: {failure.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Alert>
        )}
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid>
          <h2>{label}</h2>
        </Grid>
        {(showAuditHistoryButton || enableFranchiseManagement) && (
          <Grid sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {showAuditHistoryButton && !isEdit && (
              <Button
                analyticsConfiguration={{
                  eventName: "Navigate to Account Audit History"
                }}
                buttonStyle="secondary"
                startIcon={<ResetTimeIcon title="Audit History" />}
                onClick={() => {
                  navigate(
                    `/dealer/${accountEntity.id}/accountFranchiseAuditLog`
                  );
                }}
              >
                Audit History
              </Button>
            )}
            {enableFranchiseManagement && (
              <>
                <Button
                  analyticsConfiguration={{
                    eventName: isEdit ? "Save Changes" : "Toggle Edit Mode"
                  }}
                  buttonStyle="primary"
                  disabled={
                    isEdit ? !franchiseChanges.canSave || isSaving : false
                  }
                  isLoading={isSaving}
                  startIcon={
                    isEdit ? (
                      <CheckmarkSolidIcon title="Save Changes" />
                    ) : (
                      <EditIcon title="Edit" />
                    )
                  }
                  onClick={async () => {
                    if (isEdit) {
                      setIsSaving(true);
                      try {
                        await franchiseChanges.saveChanges();
                        setIsEdit(false);
                      } catch (error) {
                        console.error("Failed to save changes:", error);
                      } finally {
                        setIsSaving(false);
                      }
                    } else {
                      setIsEdit(!isEdit);
                    }
                  }}
                >
                  {isEdit ? "Save Changes" : "Edit"}
                </Button>
                {isEdit && (
                  <Button
                    analyticsConfiguration={{
                      eventName: "Cancel Edit Mode"
                    }}
                    buttonStyle="secondary"
                    disabled={isSaving}
                    startIcon={<XMarkIcon title="Cancel" />}
                    onClick={() => {
                      setIsEdit(false);
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </>
            )}
          </Grid>
        )}
      </Grid>
      <Grid container marginBlockEnd={2} spacing={2}>
        {isEdit && enableFranchiseManagement ? (
          <>
            <Grid
              alignItems={"center"}
              flexDirection={"column"}
              size={{ lg: 6, md: 6, xs: 12 }}
            >
              <Grid marginBottom={2} size={12}>
                <DashboardDealerInfoCard
                  accountFranchises={accountFranchises}
                  franchiseChanges={franchiseChanges}
                  isAccountFranchisesError={isAccountFranchisesError}
                  isEdit={isEdit}
                  isFetchingAccountFranchises={isFetchingAccountFranchises}
                />
              </Grid>
              <Grid size={12}>
                <DealerContact isEdit={isEdit} />
              </Grid>
            </Grid>
            <Grid
              alignItems={"center"}
              flexDirection={"column"}
              size={{ lg: 6, md: 6, xs: 12 }}
            >
              <Grid marginBottom={2} size={12}>
                <DealerAddress isEdit={isEdit} />
              </Grid>
              <Grid size={12}>
                <DealerHours isEdit={isEdit} />
              </Grid>
            </Grid>
          </>
        ) : (
          <>
            <DashboardDealerInfoCard
              accountFranchises={accountFranchises}
              isAccountFranchisesError={isAccountFranchisesError}
              isFetchingAccountFranchises={isFetchingAccountFranchises}
            />
            <DealerAddress />
            <DealerContact />
            <DealerHours />
          </>
        )}
      </Grid>
    </>
  );
};

export default DashboardOverview;
